import OneSignal from 'react-onesignal'
import axios from 'axios'
import { Courier } from '@trycourier/courier-js'
import type { AsyncAction } from 'src/state/_types'
import logger from 'src/utils/logger'

export const initializeNotifications: AsyncAction = async ({ state }) => {

    const appId = import.meta.env.VITE_ONESIGNAL_APP_ID
    if (!appId) {
        logger('OneSignal: No app ID configured, skipping initialization')
        return
    }

    try {

        await OneSignal.init({
            appId,
            allowLocalhostAsSecureOrigin: true,
            autoResubscribe: true,
            notificationClickHandlerMatch: "origin",
            notificationClickHandlerAction: "focus"
        })

        // Read current permission state
        state.notifications.permission = OneSignal.Notifications.permission ? 'granted' : 'default'

        // Check if already subscribed
        const subscriptionId = OneSignal.User.PushSubscription.id
        if (subscriptionId) {
            state.notifications.subscriptionId = subscriptionId
        }

        // Load dismissed state from localStorage
        const dismissed = localStorage.getItem('notification-prompt-dismissed')
        if (dismissed === 'true') {
            state.notifications.promptDismissed = true
        }

        // Listen for subscription changes
        OneSignal.User.PushSubscription.addEventListener('change', (event) => {
            state.notifications.subscriptionId = event.current.id ?? null
            state.notifications.permission = event.current.optedIn ? 'granted' : 'default'
        })

        // Handle notification clicks for deep linking
        OneSignal.Notifications.addEventListener('click', (event) => {
            const data = event.notification.additionalData as Record<string, string> | undefined
            if (data?.url) {
                window.location.href = data.url
            }
        })

        if (state.auth.user_id) {

            await OneSignal.login(state.auth.user_id).catch(() => {})

            OneSignal.User.addTags({
                user_id: state.auth.user_id,
                tenant_id: state.auth.tenant_id || '',
                user_type: state.auth.user_type || '',
                agency_id: state.auth.agency_id || '',
                user_role: state.auth.user_role || '',
                user_name: state.profile?.name || 'User',
                user_email: state.profile?.email || '',
            })

        }

        state.notifications.initialized = true
        logger('OneSignal: Initialized successfully')

    } catch (err) {
        logger(`OneSignal: Initialization failed - ${err}`)
    }

    // Initialize Courier in-app inbox
    if (state.auth.user_id) {
        try {

            const { data } = await axios.post(
                `${import.meta.env.VITE_AUTH_API_HOST}/notifications/courier-token`,
                { user_id: state.auth.user_id },
                { withCredentials: true },
            )

            if (data.token) {
                Courier.shared.signIn({
                    userId: state.auth.user_id,
                    jwt: data.token
                })
                logger('Courier: Initialized successfully')
            }

        } catch (err) {
            logger(`Courier: Initialization failed - ${err}`)
        }
    }

}
